// src/api/actionProposals.ts
import { apiJson, ApiError } from "./client";
import type { ActionProposal } from "@/types/actionProposals";

type PatchResponse = { ok: boolean; proposal: ActionProposal };

function patchProposal(
  messageId: string,
  proposalId: string,
  body: Record<string, unknown>
): Promise<PatchResponse> {
  return apiJson<PatchResponse>(
    `/chats/messages/${encodeURIComponent(messageId)}/action-proposals/${encodeURIComponent(proposalId)}`,
    { method: "PATCH", body: JSON.stringify(body) }
  );
}

/**
 * Claim-before-execute: transition proposed → confirming.
 *
 * Returns the claimed proposal, or — on a 409 CAS conflict (double click,
 * second tab) — the proposal's current state with `claimed: false` so the
 * caller renders that state instead of firing the mutation again.
 */
export async function claimActionProposal(
  messageId: string,
  proposalId: string
): Promise<{ claimed: boolean; proposal: ActionProposal }> {
  try {
    const res = await patchProposal(messageId, proposalId, { op: "claim" });
    return { claimed: true, proposal: res.proposal };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      const detail = e.detail as { proposal?: ActionProposal } | undefined;
      if (detail?.proposal) {
        return { claimed: false, proposal: detail.proposal };
      }
    }
    throw e;
  }
}

export function resolveActionProposal(
  messageId: string,
  proposalId: string,
  outcome:
    | { status: "confirmed"; resultRef?: Record<string, unknown> }
    | { status: "failed"; error: string }
): Promise<PatchResponse> {
  const body: Record<string, unknown> = { op: "outcome", status: outcome.status };
  if (outcome.status === "confirmed" && outcome.resultRef) {
    body.result_ref = outcome.resultRef;
  }
  if (outcome.status === "failed") {
    body.error = outcome.error;
  }
  return patchProposal(messageId, proposalId, body);
}

export function dismissActionProposal(
  messageId: string,
  proposalId: string
): Promise<PatchResponse> {
  return patchProposal(messageId, proposalId, { op: "dismiss" });
}

/**
 * Return a claimed proposal to actionable (confirming → proposed) when an
 * interactive handoff was cancelled — e.g. the user closed the connect-source
 * wizard without finishing. Lets them retry instead of leaving a dead card.
 */
export function releaseActionProposal(
  messageId: string,
  proposalId: string
): Promise<PatchResponse> {
  return patchProposal(messageId, proposalId, { op: "release" });
}
